import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import {
  Typography, IconButton, Grid, Paper, TextField, Button, MenuItem,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Snackbar,
  CircularProgress,
  Chip,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import * as Icons from '@material-ui/icons';
import DataTable from '../components/DataTable';
import QuickSearchToolbar from '../components/QuickSearchToolbar';
import { invokeApig } from '../lib/awsLib';
import NeokodeButton from '../components/NeokodeButton';

const useStyles = makeStyles((theme) => ({
  section: {
    margin: theme.spacing(3, 0),
    marginBottom: theme.spacing(10),
    width: 'inherit',
  },
  paper: {
    padding: theme.spacing(2),
  },
  breadcrumbContainer: {
    padding: theme.spacing(1),
  },
  table: {
    minWidth: 650,
  },
  addButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  addButton: {
    margin: '0',
  },
  deleteButton: {
    backgroundColor: '#f44336',
    '&:hover': {
      backgroundColor: '#f44336',
    },
  },
  resetButton: {
    backgroundColor: '#2196f3',
    '&:hover': {
      backgroundColor: '#2196f3',
    },
  },
  searchContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '20px',
  },
  searchInput: {
    marginRight: '10px',
  },
  editIcon: {
    color: '#2196f3',
  },
  lockIcon: {
    color: '#ff9800',
  },
  deleteIcon: {
    color: '#f44336',
  },
  rootSearch: {
    padding: theme.spacing(0.5, 0.5, 0),
    justifyContent: 'space-between',
    display: 'flex',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
  },
  textFieldSearch: {
    [theme.breakpoints.down('xs')]: {
      width: '100%',
    },
    margin: theme.spacing(1, 0.5, 1.5),
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(0.5),
    },
    '& .MuiInput-underline:before': {
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
  },
  buttonsDialogContainer: {
    padding: '10px 25px',
  },
  refreshIcon: {
    color: '#666',
    margin: '0 15px'
  },
  button: {
    margin: '15px',
  },
  confirmUserText: {
    textAlign: 'center',
  },
  green: {
    backgroundColor: '#00b31f',
    color: '#ffffff',
  },
  red: {
    backgroundColor: '#f44336',
    color: '#ffffff',
  },
  yellow: {
    backgroundColor: '#ff9800',
    color: '#ffffff',
  },
}));

function escapeRegExp(value) {
  return value.replace(/[-[\]{}()*+?.,\\^$|#\s]/g, '\\$&');
}

function CustomerUsersPage(props) {
  const classes = useStyles();
  const { id } = useParams();
  const [customer, setCustomer] = useState(null);
  const [isGlobalError, setIsGlobalError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCustomer, setIsLoadingCustomer] = useState(true);
  const [isError, setIsError] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [username, setUsername] = useState('');
  const [user, setUser] = useState({});
  const [isResetPassword, setIsResetPassword] = useState(false);
  const [isDeleteUser, setIsDeleteUser] = useState(false);
  const [message, setMessage] = useState('');
  const [users, setUsers] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [openToast, setOpenToast] = useState(false);
  const [messageToast, setMessageToast] = useState('');

  useEffect(() => {
    getCustomer();
    getUsers();
  }, []);

  const getCustomer = async() => {
    if (id) {
        try {
            setIsLoadingCustomer(true);
            const response = await invokeApig({
                path: `/account/${id}`
            });
            if (response.code === 0) {
              setCustomer(response.data);
            }
        } catch (error) {
          console.log(error);
        }
    }
    setIsLoadingCustomer(false);
  }

  const getUsers = async () => {
    try {
      setIsLoading(true);
      setIsGlobalError(false);
      setUsers([]);
      setSearchText('');
      const response = await invokeApig({
        path: "/account/user/find",
        method: "POST",
        body: {
          "ownerId": id
        }
      });
      if (response.code === 0) {
        setUsers(response.data)
      } else {
        if (response.code === 403) {
          setMessage(response.message);
          setIsGlobalError(true);
        } else {
          setMessage('Error al obtener los usuarios. Intenta nuevamente o comunícate con Soporte');
          setIsGlobalError(true);
        }
      }
    } catch (error) {
      console.error('Error in getAccess:', error);
      setMessage('Error al obtener los usuarios. Intenta nuevamente o comunícate con Soporte');
      setIsGlobalError(true);
    }
    setIsLoading(false);
  }

  const resetPassword = async (username) => {
    try {
      setIsError(false);
      setIsLoading(true);
      const response = await invokeApig({
        path: `/account/user/reset`,
        method: "POST",
        body: {
          id: username
        }
      });
      if (response.code !== 0) {
        setMessage('No se pudo resetear la clave. Intenta nuevamente o contacta a Soporte.');
        setIsError(true);
      } else {
        setMessageToast('Se ha enviado un correo al usuario con las instrucciones para cambiar la contraseña');
        setOpenToast(true);
        setOpenDialog(false);
      }
    } catch (error) {
      console.error('Error in getAccess:', error);
      setMessage('Ocurrió un error al resetear la clave. Por favor intenta más tarde o comunícate con Soporte');
      setIsError(true);
    }
    setIsLoading(false);
    getUsers();
  }

  const deleteUser = async (username) => {
    try {
      setIsError(false);
      setIsLoading(true);
      const response = await invokeApig({
        path: `/account/user/${username}`,
        method: "DELETE"
      });
      if (response.code !== 0) {
        setMessage('Error al eliminar el usuario. Intenta nuevamente o contacta a Soporte.');
        setIsError(true);
      } else {
        setMessageToast('Se ha eliminado el usuario correctamente');
        setOpenToast(true);
        setOpenDialog(false);
      }
    } catch (error) {
      console.error('Error in getAccess:', error);
      setMessage('Ocurrió un error al eliminar el usuario. Por favor intenta más tarde o comunícate con Soporte');
      setIsError(true);
    }
    setIsLoading(false);
    getUsers();
  }

  const requestSearch = (searchValue) => {
    setSearchText(searchValue);
  };

  // Campos por los que se busca (los que identifican al usuario).
  const SEARCH_FIELDS = ['name', 'lastName', 'email'];

  const visibleRows = useMemo(() => {
    let list = users;
    if (statusFilter === 'active') {
      list = list.filter((u) => u.enabled);
    } else if (statusFilter === 'inactive') {
      list = list.filter((u) => !u.enabled);
    }
    const term = searchText.trim();
    if (term) {
      const searchRegex = new RegExp(escapeRegExp(term), 'i');
      list = list.filter((row) =>
        SEARCH_FIELDS.some((field) => searchRegex.test(String(row[field] ?? '')))
      );
    }
    return list;
  }, [users, searchText, statusFilter]);

  const handleShowResetPassword = (username, user) => {
    setUsername(username);
    setUser(user);
    setIsResetPassword(true);
    setIsDeleteUser(false);
    setIsError(false);
    setMessage('');
    setOpenDialog(true);
  };
  const handleShowDelete = (username, user) => {
    setUsername(username);
    setUser(user);
    setIsResetPassword(false);
    setIsDeleteUser(true);
    setIsError(false);
    setMessage('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  }

  const handleConfirmAction = () => {
    if (isResetPassword) {
      resetPassword(username);
    }
    if (isDeleteUser) {
      deleteUser(username);
    }
  }

  const handleCloseToast = () => {
    setOpenToast(false);
  }

  const columns = [
    {
      id: 'name',
      label: 'Nombre',
    },
    {
      id: 'lastName',
      label: 'Apellido',
    },
    {
      id: 'email',
      label: 'Email',
    },
    {
      id: 'enabled',
      label: 'Estado',
      render: (row) => (
        row.enabled ?
        <Chip
          size="small"
          label="Activo"
          className={classes.green}
        />
        :
        <Chip
          size="small"
          label="Inactivo"
          className={classes.yellow}
        />
      )
    },
    {
      id: 'status',
      label: 'Confirmación',
      sortable: false,
    },
    {
      id: 'actions',
      label: 'Acciones',
      sortable: false,
      render: (row) => {
        return (
          <>
            <IconButton component={RouterLink} to={`/customers/${id}/users/edit/${row.username}`} className={classes.editIcon}>
              <Icons.Edit />
            </IconButton>
            <IconButton onClick={() => handleShowResetPassword(row.username, row)} className={classes.lockIcon}>
              <Icons.Lock />
            </IconButton>
            <IconButton onClick={() => handleShowDelete(row.username, row)} className={classes.deleteIcon}>
              <Icons.Delete />
            </IconButton>
          </>
        );
      },
    }
  ];

  const showConfirmDialog = () => {
    return (
      <Dialog open={openDialog} onClose={handleCloseDialog} aria-labelledby="form-dialog-title">
        <DialogTitle id="form-dialog-title">Confirmación</DialogTitle>
        <DialogContent>
          <p className={classes.confirmUserText}><b>Usuario</b>: {user.name} {user.lastName} &lt;{user.email}&gt;</p>
          <Alert severity="info">
            <p>{ isResetPassword && 'Se va a resetear la contraseña. Le llegará una correo electrónico con una contraseña temporal al usuario.' }</p>
            <p>{ isDeleteUser && 'Se va a eliminar el usuario y ya no tendrá acceso a la plataforma.' }</p>
            <p><b>¿Estás seguro que quieres continuar con la acción?</b></p>
          </Alert>
          {
            isError &&
            <Alert severity="error">
              {message}
            </Alert>
          }
        </DialogContent>
        <DialogActions className={classes.buttonsDialogContainer}>
          <Button
            type="button"
            variant="contained"
            className={classes.button}
            onClick={handleCloseDialog}
            disabled={isLoading}
          >
            No, cerra esta ventana
          </Button>
          <Button
            type="button"
            color={isDeleteUser ? "secondary" : "primary"}
            variant="contained"
            className={isDeleteUser ? classes.deleteButton : classes.resetButton}
            onClick={handleConfirmAction}
            disabled={isLoading}
          >
            Sí, confirmo
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderError = () => (
    <>
      <Alert severity="error">
          {message}
      </Alert>
      <NeokodeButton
        type="button"
        variant="contained"
        className={classes.button}
        component={RouterLink}
        to="/"
      >
        Volver al inicio
      </NeokodeButton>
    </>
  );

  const renderCustomer = () => (
    isLoadingCustomer ?
    <CircularProgress />
    :
    (
      customer ?
      <Grid container spacing={2}>
        <Grid item xs={10} md={10}>
          <Typography variant='h5'>Datos del cliente</Typography>
        </Grid>
        <Grid item xs={2} md={2}>
          <Button
            type="button"
            variant="contained"
            className={classes.button}
            component={RouterLink} to="/customers" 
          >
            Volver
          </Button>
        </Grid>

        <Grid item xs={4} md={2}>
          <b>RUT</b>
        </Grid>
        <Grid item xs={8} md={4}>
          {customer.id}
        </Grid>

        <Grid item xs={4} md={2}>
          <b>Nombre</b>
        </Grid>
        <Grid item xs={8} md={4}>
          {customer.name}
        </Grid>

        <Grid item xs={4} md={2}>
          <b>Contacto</b>
        </Grid>
        <Grid item xs={8} md={4}>
          {customer.contact}
        </Grid>

        <Grid item xs={4} md={2}>
          <b>Estado</b>
        </Grid>
        <Grid item xs={8} md={4}>
          {customer.status}
        </Grid>
      </Grid>
      :
      <Grid container spacing={2}>
        <Grid item xs={12} md={12}>
          No pudimos cargar los datos del cliente, asegúrate que es el RUT {id} es el correcto antes de continuar.
        </Grid>
      </Grid>
    )
  );

  return (
    <Grid container spacing={2} className={classes.section} justifyContent="center">
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <Paper className={classes.paper}>
          {renderCustomer()}
        </Paper>
      </Grid>
      <Grid item xs={12} sm={12} md={12} lg={12}>
        <Paper className={classes.paper}>
          <Typography variant="h4" gutterBottom>
            Administración de Usuarios
          </Typography>

          {
            isGlobalError ?
            renderError()
            :
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                <TextField
                  select
                  size="small"
                  variant="outlined"
                  label="Mostrar"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  style={{ minWidth: 170 }}
                >
                  <MenuItem value="active">Activos</MenuItem>
                  <MenuItem value="inactive">Inactivos</MenuItem>
                  <MenuItem value="all">Todos</MenuItem>
                </TextField>
                <div style={{ flex: 1, minWidth: 240 }}>
                  <QuickSearchToolbar
                    value={searchText}
                    onChange={(event) => requestSearch(event.target.value)}
                    clearSearch={() => requestSearch('')}
                    onRefresh={() => getUsers()}
                    addButtonUrl={`/customers/${id}/users/create`}
                    addButtonText="Agregar Usuario"
                  />
                </div>
              </div>
              <DataTable
                columns={columns}
                rows={visibleRows}
                getRowId={(row) => row.username}
                loading={isLoading}
                emptyMessage="Sin datos para mostrar"
                initialSort={{ orderBy: 'name', order: 'asc' }}
              />
              {showConfirmDialog()}
            </>
          }
        </Paper>
      </Grid>
      <Snackbar open={openToast} autoHideDuration={6000} onClose={handleCloseToast}>
        <Alert onClose={handleCloseToast} severity="success">
          {messageToast}
        </Alert>
      </Snackbar>
    </Grid>
  );
}

export default CustomerUsersPage;
